import { useCallback, useEffect, useState } from 'react'
import { KeyRound, Printer, RefreshCw, Settings } from 'lucide-react'
import { Papel } from '@du/shared'
import {
  Badge,
  Button,
  Escolha,
  GrupoEscolhas,
  Icone,
  Input,
  Modal,
  SectionTitle,
  Skeleton,
  StatTile,
  useAviso,
} from '@du/ui'
import { api, ErroApi } from '../lib/api.js'
import { useSessao } from '../lib/sessao.js'
import type { EventoSse } from '../lib/tempoReal.js'

/**
 * Configurar a impressora pela TELA.
 *
 * Antes isto só existia no config.json do PC do balcão, e o que o portal
 * mostrava era enfeite — trocar aqui não mudava nada. Agora o servidor manda
 * a conexão e o destino ao agente toda vez que ele conecta, então salvar
 * aqui muda a impressão de verdade.
 */
function ConfigurarImpressora({
  impressora,
  aoFechar,
  aoSalvar,
}: {
  impressora: Impressora
  aoFechar: () => void
  aoSalvar: () => void
}) {
  const [conexao, setConexao] = useState(impressora.conexao)
  const [destino, setDestino] = useState(impressora.destino)
  const [colunas, setColunas] = useState(String(impressora.colunas))
  const [escala, setEscala] = useState(impressora.escala ?? 'NORMAL')
  const dispositivos = impressora.dispositivos ?? []
  const [salvando, setSalvando] = useState(false)
  const [testando, setTestando] = useState(false)
  const [resultado, setResultado] = useState<{ ok: boolean; mensagem: string } | null>(null)
  /** Testar grava. Depois disso, "Cancelar" seria mentira. */
  const [gravou, setGravou] = useState(false)
  const { avisar } = useAviso()

  const colunasNum = Number(colunas)
  const camposOk =
    destino.trim().length > 0 && Number.isInteger(colunasNum) && colunasNum >= 24 && colunasNum <= 96

  const gravar = async () => {
    await api(`/v1/impressoras/${impressora.id}`, {
      method: 'PATCH',
      corpo: { conexao, destino: destino.trim(), colunas: colunasNum, escala },
    })
    setGravou(true)
  }

  const salvar = async () => {
    setSalvando(true)
    try {
      await gravar()
      avisar({ tom: 'sucesso', texto: 'Salvo. A impressora do balcão já está usando isto.' })
      aoSalvar()
    } catch (e) {
      avisar({ tom: 'erro', texto: e instanceof ErroApi ? e.message : 'Não consegui salvar.' })
    } finally {
      setSalvando(false)
    }
  }

  /**
   * Imprime a folha de teste.
   *
   * GRAVA ANTES de mandar. Sem isso, quem trocasse a impressora na lista e
   * clicasse em testar veria a folha sair na impressora ANTIGA — e concluiria,
   * com razão, que a tela não manda em nada.
   *
   * A resposta fica na própria janela, e não num toast que some: quem clicou
   * está em pé na frente da impressora, olhando o papel, e vai voltar os
   * olhos para a tela depois.
   */
  const testar = async () => {
    setTestando(true)
    setResultado(null)
    try {
      await gravar()
      const r = await api<{ ok: boolean; mensagem: string }>(
        `/v1/impressoras/${impressora.id}/teste`,
        { method: 'POST' },
      )
      setResultado(r)
    } catch (e) {
      setResultado({
        ok: false,
        mensagem:
          e instanceof ErroApi
            ? e.message
            : 'Não consegui mandar o teste. Confira a internet e tente de novo.',
      })
    } finally {
      setTestando(false)
    }
  }

  return (
    <Modal
      aberto
      titulo={`Configurar ${impressora.nome}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <Button variante="ghost" onClick={gravou ? aoSalvar : aoFechar}>
            {gravou ? 'Fechar' : 'Cancelar'}
          </Button>
          <Button
            variante="secundario"
            icone={Printer}
            onClick={() => void testar()}
            carregando={testando}
            disabled={!camposOk || salvando}
          >
            Imprimir teste
          </Button>
          <Button onClick={() => void salvar()} carregando={salvando} disabled={!camposOk || testando}>
            Salvar
          </Button>
        </>
      }
    >
      {/*
        O resultado do teste fica no TOPO da janela, antes dos campos: é a
        resposta da última coisa que a pessoa fez, e procurar por ela no fim
        de um formulário rolado é o tipo de detalhe que faz desistir.
      */}
      {testando ? (
        <p className="nota-alerta">
          Mandando para a impressora… a folha pode levar alguns segundos para sair.
        </p>
      ) : resultado ? (
        <p className={`nota-alerta ${resultado.ok ? 'nota-alerta--ok' : 'nota-alerta--erro'}`}>
          {resultado.mensagem}
        </p>
      ) : null}
      {/*
        A lista que o PRÓPRIO computador do balcão mandou.
        É o caminho fácil: clicar no nome da impressora em vez de descobrir
        qual é a porta USB. Só aparece depois que o agente conectou uma vez —
        antes disso não há como saber o que existe lá.
      */}
      {dispositivos.length > 0 ? (
        <GrupoEscolhas
          nome="Impressoras neste computador"
          min={0}
          max={1}
          escolhidos={dispositivos.some((d) => d.destino === destino) ? 1 : 0}
        >
          {dispositivos.map((d) => (
            <Escolha
              key={d.nome}
              tipo="radio"
              nome="dispositivo"
              valor={d.destino}
              rotulo={d.nome}
              {...(!d.compartilhada
                ? {
                    descricao:
                      'Não dá para usar ainda: esta impressora não está compartilhada no Windows. Nas propriedades dela, aba Compartilhamento, marque "Compartilhar esta impressora" e dê um nome sem espaço (ex.: DIMEP).',
                  }
                : d.padrao
                  ? { descricao: 'padrão do Windows' }
                  : {})}
              desabilitada={!d.compartilhada}
              marcada={d.compartilhada && destino === d.destino}
              onMudar={() => {
                setDestino(d.destino)
                setConexao('USB')
              }}
            />
          ))}

          {/*
            Aviso de cima, antes da lista, quando NENHUMA serve. Sem ele a
            pessoa olha quatro opções cinzentas e conclui que o sistema
            quebrou — foi o que aconteceu no balcão.
          */}
          {dispositivos.every((d) => !d.compartilhada) ? (
            <p className="nota-alerta nota-alerta--erro">
              Nenhuma impressora deste computador está compartilhada, e sem isso o Windows não
              deixa o programa escrever nela. Compartilhe a sua nas propriedades da impressora e
              clique em Atualizar aqui. Se ela tiver entrada de rede, ligar no roteador é mais
              confiável e dispensa compartilhamento.
            </p>
          ) : null}
        </GrupoEscolhas>
      ) : (
        <p className="dash-nota">
          Assim que o programa do balcão conectar uma vez, as impressoras
          daquele computador aparecem aqui para escolher pelo nome.
        </p>
      )}

      <GrupoEscolhas nome="Ou aponte na mão" min={1} max={1} obrigatorio escolhidos={1}>
        <Escolha
          tipo="radio"
          nome="conexao"
          valor="USB"
          rotulo="Cabo USB no computador"
          descricao="O computador precisa ficar ao lado da impressora"
          marcada={conexao === 'USB'}
          onMudar={() => {
            setConexao('USB')
            if (destino.includes(':')) setDestino('USB001')
          }}
        />
        <Escolha
          tipo="radio"
          nome="conexao"
          valor="REDE"
          rotulo="Cabo de rede no roteador"
          descricao="O computador pode ficar em qualquer lugar da loja"
          marcada={conexao === 'REDE'}
          onMudar={() => {
            setConexao('REDE')
            if (!destino.includes(':')) setDestino('192.168.0.50:9100')
          }}
        />
      </GrupoEscolhas>

      <Input
        rotulo={conexao === 'REDE' ? 'IP da impressora' : 'Porta no Windows'}
        value={destino}
        onChange={(e) => setDestino(e.target.value)}
        dica={
          conexao === 'REDE'
            ? 'Com :9100 no fim. O IP precisa ser fixo — se o roteador trocar, a impressão para sozinha.'
            : 'Painel de Controle → Dispositivos e Impressoras → botão direito → Propriedades → aba Portas'
        }
      />

      <Input
        rotulo="Largura do papel"
        type="number"
        value={colunas}
        onChange={(e) => setColunas(e.target.value)}
        dica="48 para bobina de 80 mm (o caso da DIMEP) · 32 para 58 mm"
      />

      <GrupoEscolhas nome="Tamanho da letra" min={1} max={1} obrigatorio escolhidos={1}>
        <Escolha
          tipo="radio"
          nome="escala"
          valor="PEQUENA"
          rotulo="Pequena"
          descricao="Economiza papel"
          marcada={escala === 'PEQUENA'}
          onMudar={() => setEscala('PEQUENA')}
        />
        <Escolha
          tipo="radio"
          nome="escala"
          valor="NORMAL"
          rotulo="Normal"
          marcada={escala === 'NORMAL'}
          onMudar={() => setEscala('NORMAL')}
        />
        <Escolha
          tipo="radio"
          nome="escala"
          valor="GRANDE"
          rotulo="Grande"
          descricao="Para ler de longe, com a chapa quente"
          marcada={escala === 'GRANDE'}
          onMudar={() => setEscala('GRANDE')}
        />
      </GrupoEscolhas>

      <p className="dash-nota">
        <strong>Imprimir teste</strong> grava o que está nesta tela e manda uma folha para a
        impressora. A folha tem uma régua: se ela couber exata na largura do papel, sem sobrar
        nem faltar, a largura está certa.
      </p>
    </Modal>
  )
}

type Impressora = {
  id: string
  nome: string
  via: string
  conexao: string
  destino: string
  colunas: number
  escala?: string
  /** Impressoras que o PC do agente enxerga, mandadas por ele ao conectar. */
  dispositivos?:
    | { nome: string; destino: string; padrao: boolean; compartilhada: boolean }[]
    | null
  online: boolean
  ativo: boolean
  ultimoPing: string | null
}
type Job = {
  id: string
  via: string
  status: string
  tentativas: number
  erro: string | null
  criadoEm: string
  impressora: { nome: string; online: boolean }
  pedido: { numeroDia: number }
}

const TOM: Record<string, 'sucesso' | 'alerta' | 'erro' | 'neutro'> = {
  CONFIRMADO: 'sucesso',
  PENDENTE: 'alerta',
  ENVIADO: 'alerta',
  FALHOU: 'erro',
  CANCELADO: 'neutro',
}

export function Impressao({ registrar }: { registrar: (fn: (e: EventoSse) => void) => void }) {
  const { usuario } = useSessao()
  const { avisar } = useAviso()
  const [impressoras, setImpressoras] = useState<Impressora[]>([])
  const [jobs, setJobs] = useState<Job[]>([])
  const [carregando, setCarregando] = useState(true)
  const [tokenNovo, setTokenNovo] = useState<{ nome: string; token: string } | null>(null)
  const [configurando, setConfigurando] = useState<Impressora | null>(null)

  const gestor = usuario?.papel === Papel.ADMIN || usuario?.papel === Papel.GERENTE

  const carregar = useCallback(async () => {
    try {
      const [i, j] = await Promise.all([
        api<Impressora[]>('/v1/impressoras'),
        api<Job[]>('/v1/jobs-impressao?limite=30'),
      ])
      setImpressoras(i)
      setJobs(j)
    } catch (e) {
      avisar({ tom: 'erro', texto: 'Não consegui carregar', detalhe: e instanceof ErroApi ? e.message : undefined })
    } finally {
      setCarregando(false)
    }
  }, [avisar])

  useEffect(() => {
    void carregar()
  }, [carregar])

  // Status de impressora e de job chegam por SSE: a loja precisa ver a
  // impressora cair no instante em que cai, não no próximo F5.
  useEffect(() => {
    registrar((e) => {
      if (e.tipo === 'impressora.status' || e.tipo === 'impressao.status') void carregar()
    })
  }, [registrar, carregar])

  if (carregando) return <div className="pagina-gestao"><Skeleton altura={240} /></div>

  const offline = impressoras.filter((i) => i.ativo && !i.online)
  const falhas = jobs.filter((j) => j.status === 'FALHOU')

  return (
    <div className="pagina-gestao">
      {falhas.length > 0 ? (
        <p className="nota-alerta nota-alerta--erro">
          {falhas.length} comanda(s) não saíram. Confira papel, cabo e se o agente está aberto no PC do caixa.
        </p>
      ) : null}

      <section className="bloco-gestao">
        <SectionTitle
          variante="laranja"
          tamanho="sm"
          acao={<Button variante="ghost" tamanho="sm" icone={RefreshCw} onClick={() => void carregar()}>Atualizar</Button>}
        >
          Impressoras
        </SectionTitle>

        <div className="grade-stats">
          <StatTile rotulo="Conectadas" valor={String(impressoras.filter((i) => i.online).length)} tom="marca" />
          <StatTile rotulo="Fora do ar" valor={String(offline.length)} tom={offline.length > 0 ? 'destaque' : 'neutro'} />
          <StatTile rotulo="Comandas hoje" valor={String(jobs.length)} apoio={`${falhas.length} com falha`} />
        </div>

        <ul className="lista-simples">
          {impressoras.map((i) => (
            <li key={i.id}>
              <span>
                <Icone de={Printer} tamanho={16} /> {i.nome}
                <Badge tom={i.online ? 'sucesso' : 'erro'}>{i.online ? 'conectada' : 'fora do ar'}</Badge>
              </span>
              <span className="lista-simples__fraco">
                {i.conexao === 'REDE' ? 'Rede' : 'USB'} · <code>{i.destino}</code> · {i.via} ·{' '}
                {i.colunas} col
              </span>
              {gestor ? (
                <span className="lista-simples__acoes">
                  <Button
                    variante="ghost"
                    tamanho="sm"
                    icone={KeyRound}
                    onClick={async () => {
                      try {
                        const r = await api<{ token: string }>(`/v1/impressoras/${i.id}/token`, { method: 'POST' })
                        setTokenNovo({ nome: i.nome, token: r.token })
                        await carregar()
                      } catch (e) {
                        avisar({ tom: 'erro', texto: 'Não consegui gerar', detalhe: e instanceof ErroApi ? e.message : undefined })
                      }
                    }}
                  >
                    Novo token
                  </Button>
                  <Button
                    variante="ghost"
                    tamanho="sm"
                    icone={Settings}
                    onClick={() => setConfigurando(i)}
                  >
                    Configurar
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <section className="bloco-gestao">
        <SectionTitle variante="vazado" tamanho="sm">Últimas comandas</SectionTitle>
        {jobs.length === 0 ? (
          <p className="nota-alerta">Nenhuma comanda ainda hoje.</p>
        ) : (
          <ul className="lista-simples">
            {jobs.map((j) => (
              <li key={j.id}>
                <span>
                  #{String(j.pedido.numeroDia).padStart(3, '0')} · {j.via}
                  <Badge tom={TOM[j.status] ?? 'neutro'}>{j.status}</Badge>
                </span>
                <span className="lista-simples__fraco">
                  {new Date(j.criadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  {j.tentativas > 0 ? ` · ${j.tentativas} tentativa(s)` : ''}
                </span>
                {j.erro ? <span className="lista-simples__erro">{j.erro}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {configurando ? (
        <ConfigurarImpressora
          impressora={configurando}
          aoFechar={() => setConfigurando(null)}
          aoSalvar={() => {
            setConfigurando(null)
            void carregar()
          }}
        />
      ) : null}

      <Modal
        aberto={tokenNovo !== null}
        aoFechar={() => setTokenNovo(null)}
        titulo={`Token de ${tokenNovo?.nome ?? ''}`}
        rodape={<Button onClick={() => setTokenNovo(null)}>Copiei</Button>}
      >
        {/*
          Passo a passo, e não só "cole no config.json". Quem instala está no
          balcão, com o computador na frente, e precisa saber ONDE o arquivo
          aparece — ele só nasce depois da primeira execução, o que não é
          óbvio para ninguém.
        */}
        <p>No computador do caixa, com este token copiado:</p>
        <ol className="passos">
          <li>
            Dois cliques em <strong>iniciar.bat</strong>. Na primeira vez ele cria um arquivo
            <strong> config.json</strong> na mesma pasta e para, dizendo que falta o token.
          </li>
          <li>
            Abra esse <strong>config.json</strong> no Bloco de Notas e troque
            <code> cole-aqui-o-token-gerado-no-portal </code> por este token, mantendo as aspas.
          </li>
          <li>
            Salve, feche, e dois cliques em <strong>iniciar.bat</strong> de novo. A janela preta
            tem que dizer <strong>conectado</strong>.
          </li>
        </ol>
        {/* Aparece UMA vez. O servidor guarda só o hash — nem o ADMIN consegue
            ver de novo depois de fechar esta janela. */}
        <pre className="token">{tokenNovo?.token}</pre>
        <p className="nota-alerta">
          Esta é a única vez que o token aparece. Se fechar sem copiar, gere outro.
        </p>
      </Modal>
    </div>
  )
}
